import { env } from '@/app/config'

/**
 * Avatars come in two flavours.
 *
 * Eight preset photographs in the app's own register: dusk, amber, limestone,
 * a cat that is never the centre of a cartoon. A person can be any of them
 * without uploading anything — key "preset:<id>".
 *
 * Or their own picture (§0061): a 512×512 JPEG the client crops from the
 * camera or the gallery, stored in the public `avatars` bucket under the
 * person's own folder — key "img:<uid>/<file>.jpg".
 */
export const AVATAR_PRESETS = [
  { id: 'tabby-dusk', label: 'Atigrado al atardecer' },
  { id: 'green-eye', label: 'Ojo verde' },
  { id: 'patch-eye', label: 'Parche' },
  { id: 'ginger-chair', label: 'Siesta en la silla' },
  { id: 'shutters', label: 'Tras la persiana' },
  { id: 'alley-gold', label: 'Callejón dorado' },
  { id: 'rooftop-night', label: 'Tejado de noche' },
  { id: 'terrace-dusk', label: 'Terraza al anochecer' },
] as const

export type AvatarPresetId = (typeof AVATAR_PRESETS)[number]['id']

export const AVATAR_BUCKET = 'avatars'
export const AVATAR_SIZE = 512

/** Resolves an avatar_key to something an <img> can show, or null for initials. */
export function avatarSrc(key: string | null | undefined): string | null {
  if (!key) return null
  if (key.startsWith('preset:')) {
    const id = key.slice('preset:'.length)
    return AVATAR_PRESETS.some((p) => p.id === id) ? '/avatars/' + id + '.jpg' : null
  }
  if (key.startsWith('img:')) {
    return env.supabaseUrl + '/storage/v1/object/public/' + AVATAR_BUCKET + '/' + key.slice('img:'.length)
  }
  return null
}

/** The storage path behind an "img:" key, or null for anything else. */
export function avatarPath(key: string | null | undefined): string | null {
  return key && key.startsWith('img:') ? key.slice('img:'.length) : null
}

/**
 * Centre-crops a picked image to a square and shrinks it to AVATAR_SIZE as a
 * JPEG. Browsers already honour EXIF orientation when decoding, so a phone
 * photo lands the right way up.
 */
export async function squareAvatarJpeg(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('unreadable_image'))
      el.src = url
    })
    const side = Math.min(img.naturalWidth, img.naturalHeight)
    if (!side) throw new Error('unreadable_image')
    const sx = (img.naturalWidth - side) / 2
    const sy = (img.naturalHeight - side) / 2
    const canvas = document.createElement('canvas')
    canvas.width = AVATAR_SIZE
    canvas.height = AVATAR_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no_canvas')
    ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE)
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('encode_failed'))), 'image/jpeg', 0.86),
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}
